import { useState, useEffect } from 'react';
import { useForm, Controller, type ControllerRenderProps } from 'react-hook-form';
import { useApolloClient, useLazyQuery } from '@apollo/client';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Label,
  Input,
  Textarea,
  Button,
  TagInput,
  CloseButton,
  Switch,
} from '@artstorefronts/ui';
import { toast } from 'sonner';
import { TrashIcon, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';
import { useImages } from '~/context/image';
import { useAnalytics } from '../context/analytics';
import { validateTagName } from '../utils';
import DimensionsInput from './DimensionsInput';
import {
  ArtworkSpecsFormSection,
  artworkSpecsToFormValues,
  buildSpecsMetaInput,
  EMPTY_SPECS_FORM_VALUES,
  type ArtworkSpecsFormValues,
  specsFormChanged,
} from './ArtworkSpecsFormSection';
import { ARTWORK_EDIT_IMAGE_QUERY, CHECK_IMAGE_SLUG_AVAILABILITY } from '../graphql/queries';
import { useTranslation } from 'react-i18next';

const TITLE_PLACEHOLDER_KEYS: Record<string, string> = {
  image: 'artwork-form.title-placeholder-image',
  collection: 'artwork-form.title-placeholder-collection',
  series: 'artwork-form.title-placeholder-series',
};

const DESCRIPTION_PLACEHOLDER_KEYS: Record<string, string> = {
  image: 'artwork-form.description-placeholder-image',
  collection: 'artwork-form.description-placeholder-collection',
  series: 'artwork-form.description-placeholder-series',
};

const DELETE_SUCCESS_KEYS: Record<string, string> = {
  image: 'artwork-form.delete-image-success',
  collection: 'artwork-form.delete-collection-success',
  series: 'artwork-form.delete-series-success',
};

const DELETE_ERROR_KEYS: Record<string, string> = {
  image: 'artwork-form.delete-image-error',
  collection: 'artwork-form.delete-collection-error',
  series: 'artwork-form.delete-series-error',
};

const FORM_TITLE_KEYS: Record<string, string> = {
  image: 'artwork-form.title-image',
  collection: 'artwork-form.title-collection',
  series: 'artwork-form.title-series',
};

interface Tag {
  id: string;
  name: string;
}

interface FormData {
  title: string;
  description: string;
  tags: Tag[];
  height?: number;
  width?: number;
  dimensionType?: 'inches' | 'centimeters';
  isPublic?: boolean;
  slug?: string;
  specs: ArtworkSpecsFormValues;
}

interface ImageArtworkSpecs {
  mediumType?: string | null;
  medium?: string | null;
  technique?: string | null;
  subMedium?: string | null;
  software?: string | null;
  camera?: string | null;
  lens?: string | null;
  focalLength?: string | null;
  aperture?: string | null;
  shutterSpeed?: string | null;
  iso?: string | null;
  mediumCategoryId?: string | null;
  hidden?: string[] | null;
}

interface ImageItem {
  id: string;
  title?: string;
  description?: string;
  tags?: Tag[];
  height?: number;
  width?: number;
  dimensionType?: 'inches' | 'centimeters';
  isPublic?: boolean;
  slug?: string;
  thumbnailUrl?: string;
  artworkSpecs?: ImageArtworkSpecs | null;
}

interface CollectionItem {
  id: string;
  name?: string;
  description?: string;
}

interface ArtworkDetailsFormProps {
  image?: ImageItem;
  collection?: CollectionItem & { id: string };
  series?: CollectionItem & { id: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type?: 'modal' | 'sheet' | null;
  contentType: 'image' | 'collection' | 'series';
  imageOriginalWidth?: number;
  imageOriginalHeight?: number;
  setTriggerTimestamp?: (timestamp: number) => void;
}

function buildFormValues(
  contentType: 'image' | 'collection' | 'series',
  image?: ImageItem,
  collection?: CollectionItem,
  series?: CollectionItem
): FormData {
  if (contentType === 'image') {
    return {
      title: image?.title || '',
      description: image?.description || '',
      tags: image?.tags || [],
      height: image?.height,
      width: image?.width,
      dimensionType: image?.dimensionType,
      isPublic: image?.isPublic ?? true,
      slug: image?.slug,
      specs: artworkSpecsToFormValues(image?.artworkSpecs),
    };
  }

  if (contentType === 'collection') {
    return {
      title: collection?.name || '',
      description: collection?.description || '',
      tags: [],
      specs: { ...EMPTY_SPECS_FORM_VALUES },
    };
  }

  return {
    title: series?.name || '',
    description: series?.description || '',
    tags: [],
    specs: { ...EMPTY_SPECS_FORM_VALUES },
  };
}

function ArtworkDetailsForm({
  image,
  collection,
  series,
  open = false,
  onOpenChange,
  type = 'sheet',
  contentType,
  imageOriginalWidth,
  imageOriginalHeight,
  setTriggerTimestamp,
}: ArtworkDetailsFormProps) {
  const { t } = useTranslation('artwork');
  const { t: tc } = useTranslation('common');
  const { amplitude } = useAnalytics();
  const { updateImage, updateCollection, updateSeries, deleteImage, deleteCollection, deleteSeries, refetchData } =
    useImages();
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const apolloClient = useApolloClient();

  // Slug validation
  const [checkImageSlugAvailability] = useLazyQuery(CHECK_IMAGE_SLUG_AVAILABILITY);
  const [slugValidation, setSlugValidation] = useState<{
    isValid: boolean;
    isChecking: boolean;
    error?: string;
  }>({ isValid: true, isChecking: false });

  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    reset,
    register,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: buildFormValues(contentType, image, collection, series),
  });

  const isPublic = watch('isPublic');

  useEffect(() => {
    if (!open) {
      setIsRefreshing(false);
      reset(buildFormValues(contentType, image, collection, series));
      setSlugValidation({ isValid: true, isChecking: false });
      return;
    }

    let cancelled = false;

    // The full row by id: the library holds only loaded, slim pages.
    async function fetchImageRow() {
      if (!image?.id) {
        return image;
      }

      const { data } = await apolloClient.query({
        query: ARTWORK_EDIT_IMAGE_QUERY,
        variables: { id: image.id },
        fetchPolicy: 'network-only',
      });
      return data?.image ?? image;
    }

    async function refreshOnOpen() {
      if (contentType === 'series') {
        if (cancelled) {
          return;
        }
        reset(buildFormValues(contentType, image, collection, series));
        return;
      }

      setIsRefreshing(true);
      try {
        if (contentType === 'image') {
          const freshImage = await fetchImageRow();
          if (!cancelled) {
            reset(buildFormValues(contentType, freshImage, collection, series));
          }
          return;
        }

        const result = await refetchData();
        if (cancelled) {
          return;
        }

        const freshCollection =
          result?.data?.collections?.find((c) => String(c.id) === String(collection?.id)) ?? collection;
        reset(buildFormValues(contentType, image, freshCollection, series));
      } catch (error) {
        console.error('Error refreshing artwork details on open:', error);
        if (!cancelled) {
          reset(buildFormValues(contentType, image, collection, series));
        }
      } finally {
        if (!cancelled) {
          setIsRefreshing(false);
        }
      }
    }

    void refreshOnOpen();

    return () => {
      cancelled = true;
    };
  }, [open, contentType, image?.id, collection?.id, series?.id, reset, refetchData, apolloClient]);

  async function onSubmit(values: FormData) {
    try {
      setIsSubmitting(true);

      // Prevent empty slug submission if image already has a slug
      if (contentType === 'image' && image?.slug && (!values.slug || values.slug.trim() === '')) {
        toast.error(t('artwork-form.slug-error-empty'), {
          position: 'top-right',
        });
        setIsSubmitting(false);
        return;
      }

      // Validate slug if it's an image and slug is provided
      if (contentType === 'image' && values.slug && values.slug !== image?.slug) {
        const isSlugValid = await validateSlug(values.slug);
        if (!isSlugValid) {
          toast.error(t('artwork-form.slug-error-fix'), {
            position: 'top-right',
          });
          setIsSubmitting(false);
          return;
        }
      }

      const input = {
        id: contentType === 'image' ? image!.id : contentType === 'collection' ? collection!.id : series!.id,
        ...values,
        tags: values.tags.map((tag: Tag) => tag.name),
      };

      // Check if width or height changed for images
      const dimensionsChanged =
        contentType === 'image' &&
        (image?.width !== input.width ||
          image?.height !== input.height ||
          image?.dimensionType !== input.dimensionType);

      if (contentType === 'image') {
        const initialSpecs = artworkSpecsToFormValues(image?.artworkSpecs);
        const specsChanged = specsFormChanged(values.specs, initialSpecs);

        await updateImage(input.id, {
          title: input.title,
          description: input.description,
          tags: values.tags.map((tag: Tag) => tag.name),
          height: input.height,
          width: input.width,
          dimensionType: input.dimensionType,
          isPublic: input.isPublic,
          slug: input.slug,
          ...(specsChanged ? { specsMeta: buildSpecsMetaInput(values.specs, initialSpecs) } : {}),
        });
      } else if (contentType === 'collection') {
        await updateCollection(input.id, { name: input.title, description: input.description });
      } else if (contentType === 'series') {
        await updateSeries(input.id, { name: input.title, description: input.description });
      }

      amplitude.track('details_saved', { tags_list: input.tags });

      toast.success(t('artwork-form.save-success'), {
        position: 'top-right',
      });
      onOpenChange(false);

      // If dimensions changed, refresh the content to rebuild the art component
      if (dimensionsChanged && setTriggerTimestamp) {
        setTriggerTimestamp(Date.now());
      }
    } catch (e) {
      console.error(e);
      toast.error(t('artwork-form.save-error'), {
        position: 'top-right',
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleDelete = async () => {
    try {
      setDeleting(true);
      if (contentType === 'image') {
        await deleteImage(image!.id);
      } else if (contentType === 'collection') {
        await deleteCollection(collection!.id);
      } else if (contentType === 'series') {
        await deleteSeries(series!.id);
      }
      toast.success(t(DELETE_SUCCESS_KEYS[contentType]), {
        id: 'delete-success-toast',
      });
      onOpenChange(false);
      navigate('/');
    } catch (e) {
      console.error(e);
      toast.error(t(DELETE_ERROR_KEYS[contentType]));
    } finally {
      setDeleting(false);
      setShowConfirmDelete(false);
    }
  };

  const handleAddTag = (tag: string, field: ControllerRenderProps<FormData, 'tags'>) => {
    const tagExists = field.value.some((existingTag: Tag) => existingTag.name.toLowerCase() === tag.toLowerCase());

    if (tagExists) {
      setError('tags', { message: t('artwork-form.tag-duplicate') });
      return;
    }

    const error = validateTagName(tag);
    if (error) {
      setError('tags', { message: error });
      return;
    }

    setError('tags', { message: undefined });
    field.onChange([...field.value, { id: tag, name: tag }]);
  };

  // Slug validation function
  const validateSlug = async (slug: string): Promise<boolean> => {
    if (!slug || slug.length < 3) {
      setSlugValidation({ isValid: false, isChecking: false, error: t('artwork-form.slug-error-min') });
      return false;
    }

    // Basic format validation
    if (!/^[a-z0-9-]+$/.test(slug)) {
      setSlugValidation({
        isValid: false,
        isChecking: false,
        error: t('artwork-form.slug-error-format'),
      });
      return false;
    }

    if (slug.includes('--') || slug.startsWith('-') || slug.endsWith('-')) {
      setSlugValidation({
        isValid: false,
        isChecking: false,
        error: t('artwork-form.slug-error-hyphens'),
      });
      return false;
    }

    // If slug hasn't changed, it's valid
    if (slug === image?.slug) {
      setSlugValidation({ isValid: true, isChecking: false });
      return true;
    }

    setSlugValidation({ isValid: true, isChecking: true });

    try {
      const { data } = await checkImageSlugAvailability({
        variables: {
          slug,
          excludeImageId: image?.id,
        },
      });

      const isAvailable = data?.checkImageSlugAvailability;
      if (isAvailable) {
        setSlugValidation({ isValid: true, isChecking: false });
        return true;
      } else {
        setSlugValidation({ isValid: false, isChecking: false, error: t('artwork-form.slug-error-taken') });
        return false;
      }
    } catch (error) {
      console.error('Error checking URL availability:', error);
      setSlugValidation({ isValid: false, isChecking: false, error: t('artwork-form.slug-error-check') });
      return false;
    }
  };

  // Simple debounce utility
  const useDebounce = <T extends unknown[]>(callback: (...args: T) => void, delay: number) => {
    const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

    return (...args: T) => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      setDebounceTimer(setTimeout(() => callback(...args), delay));
    };
  };

  // Debounced slug validation
  const debouncedSlugValidation = useDebounce((slug: string) => {
    if (slug && slug !== image?.slug) {
      validateSlug(slug);
    }
  }, 500);

  const renderFormContent = () => (
    <form
      id="artwork-details-form"
      onSubmit={handleSubmit(onSubmit)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.target as HTMLElement).id !== 'description') {
          e.preventDefault();
        }
      }}
      className="bg-white rounded-lg flex flex-col justify-between flex-grow overflow-y-auto pb-[70px] px-4 relative"
    >
      {isRefreshing && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
          <div className="text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-indigo-600" />
            <p className="text-sm text-gray-600">{tc('refreshing')}</p>
          </div>
        </div>
      )}
      <fieldset disabled={isRefreshing} className="border-0 p-0 m-0 min-w-0">
        <div>
          {contentType === 'image' && image?.thumbnailUrl && (
            <div className="mb-4">
              <img
                src={image.thumbnailUrl}
                alt={image.title || t('artwork-form.thumbnail-alt')}
                className="rounded-lg max-h-40"
              />
            </div>
          )}
          <div className="mb-4">
            <Label htmlFor="title" className="font-bold">
              {t('artwork-form.title-label')}
            </Label>
            <Input
              {...register('title')}
              id="title"
              placeholder={t(TITLE_PLACEHOLDER_KEYS[contentType])}
              className="mt-2"
            />
          </div>
          <div className="mb-4">
            <Label htmlFor="description" className="font-bold">
              {t('artwork-form.description-label')}
            </Label>
            <Textarea
              {...register('description')}
              id="description"
              className="rounded-md mt-2"
              placeholder={t(DESCRIPTION_PLACEHOLDER_KEYS[contentType])}
            />
          </div>
          {contentType === 'image' && (
            <div className="mb-4">
              <Label htmlFor="isPublic" className="font-bold">
                {t('artwork-form.public-label')}
              </Label>
              <div className="mb-2 flex items-center">
                <p className="text-sm text-gray-500 flex-1">{t('artwork-form.public-description')}</p>
                <Controller
                  name="isPublic"
                  control={control}
                  render={({ field }) => (
                    <Switch checked={field.value ?? true} onCheckedChange={field.onChange} id="isPublic" />
                  )}
                />
              </div>
              {isPublic && (
                <div className="mt-4">
                  <Label htmlFor="slug" className="font-bold">
                    {t('artwork-form.custom-url-label')}
                  </Label>
                  <p className="text-sm text-gray-500 mb-2">{t('artwork-form.custom-url-hint')}</p>
                  <Controller
                    name="slug"
                    control={control}
                    render={({ field }) => (
                      <div>
                        <Input
                          {...field}
                          value={field.value || ''}
                          id="slug"
                          placeholder={t('artwork-form.custom-url-placeholder')}
                          className="mt-2"
                          onChange={(e) => {
                            field.onChange(e);
                            debouncedSlugValidation(e.target.value);
                          }}
                        />
                        {slugValidation.isChecking && (
                          <p className="text-sm text-gray-500 mt-1">{t('artwork-form.slug-checking')}</p>
                        )}
                        {!slugValidation.isValid && slugValidation.error && (
                          <p className="text-sm text-red-500 mt-1">{slugValidation.error}</p>
                        )}
                        {slugValidation.isValid &&
                          !slugValidation.isChecking &&
                          field.value &&
                          field.value !== image?.slug && (
                            <p className="text-sm text-green-500 mt-1">{t('artwork-form.slug-available')}</p>
                          )}
                      </div>
                    )}
                  />
                </div>
              )}
            </div>
          )}
          {contentType === 'image' && (
            <div className="mb-4">
              <Label htmlFor="tags" className="font-bold">
                {t('artwork-form.tags-label')}
              </Label>
              <Controller
                name="tags"
                control={control}
                render={({ field }) => (
                  <TagInput
                    {...field}
                    selectedTags={field.value ? field.value?.map((tag) => tag.name) : []}
                    onAddTag={(tag: string) => handleAddTag(tag, field)}
                    onRemoveTag={(tag: string) => field.onChange(field.value.filter((t) => t.name !== tag))}
                    error={errors.tags?.message}
                  />
                )}
              />
            </div>
          )}
          {contentType === 'image' && (
            <DimensionsInput
              control={control}
              heightRegister={register('height', {
                setValueAs: (value) => {
                  return value === '' || value === null || value === undefined ? undefined : Number(value);
                },
                validate: (value: number | undefined) => {
                  if (
                    value !== undefined &&
                    value !== null &&
                    value !== ('' as unknown) &&
                    !isNaN(value) &&
                    value <= 0
                  ) {
                    return t('artwork-form.height-error');
                  }
                  return true;
                },
              })}
              widthRegister={register('width', {
                setValueAs: (value) => {
                  return value === '' || value === null || value === undefined ? undefined : Number(value);
                },
                validate: (value: number | undefined) => {
                  if (
                    value !== undefined &&
                    value !== null &&
                    value !== ('' as unknown) &&
                    !isNaN(value) &&
                    value <= 0
                  ) {
                    return t('artwork-form.width-error');
                  }
                  return true;
                },
              })}
              dimensionTypeRegister={register('dimensionType')}
              widthError={errors.width?.message}
              heightError={errors.height?.message}
              originalWidth={imageOriginalWidth}
              originalHeight={imageOriginalHeight}
              onWidthChange={(calculatedWidth) => {
                setValue('width', calculatedWidth || undefined);
              }}
              onHeightChange={(calculatedHeight) => {
                setValue('height', calculatedHeight || undefined);
              }}
            />
          )}
          {contentType === 'image' && (
            <ArtworkSpecsFormSection control={control} register={register} setValue={setValue} watch={watch} />
          )}
        </div>
      </fieldset>
    </form>
  );

  const renderButtons = (context: 'sheet' | 'modal' | 'inline') => (
    <div
      className={`flex items-center bg-white p-4 justify-between gap-2 ${context !== 'inline' ? 'absolute bottom-0 left-0 right-0' : 'mt-4 border-t'}`}
    >
      <Button
        variant="outline"
        type="button"
        size="lg"
        onClick={(e) => {
          e.preventDefault();
          setShowConfirmDelete(true);
        }}
        className={`text-red-500 border-red-500 hover:bg-red-500 hover:text-white px-4 ${context !== 'modal' ? 'mr-4' : ''}`}
        disabled={deleting}
      >
        {deleting ? tc('deleting') : <TrashIcon className="size-4" />}
      </Button>
      <Button
        type="submit"
        size="lg"
        className={context !== 'modal' ? 'flex-1' : ''}
        form="artwork-details-form"
        disabled={isSubmitting || isRefreshing}
      >
        {isSubmitting ? (
          <div className="flex items-center gap-2">
            <Loader2 className="size-4 animate-spin" />
            <span>{tc('saving')}</span>
          </div>
        ) : (
          tc('save')
        )}
      </Button>
    </div>
  );

  const renderConfirmDialog = () => (
    <ConfirmDeleteDialog
      open={showConfirmDelete}
      onOpenChange={setShowConfirmDelete}
      isLoading={deleting}
      onConfirm={handleDelete}
      count={1}
      itemName={contentType}
    />
  );

  if (type === 'modal') {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl p-0" aria-describedby="artwork-details-description">
          <DialogHeader className="p-4 border-b">
            <DialogTitle className="text-2xl font-bold">{t(FORM_TITLE_KEYS[contentType])}</DialogTitle>
            <div className="absolute top-4 right-4">
              <CloseButton onClick={() => onOpenChange(false)} size="md" />
            </div>
          </DialogHeader>
          <div className="p-4 max-h-[70vh] overflow-y-auto pb-[90px]">{renderFormContent()}</div>
          <DialogFooter className="p-4 border-t absolute bottom-0 left-0 right-0 bg-white">
            {renderButtons('modal')}
          </DialogFooter>
          {renderConfirmDialog()}
        </DialogContent>
      </Dialog>
    );
  }

  if (type === 'sheet') {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="flex flex-col justify-between [&>button]:hidden px-0 w-full md:w-sm z-[100000]"
          aria-describedby={undefined}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <SheetTitle className="text-2xl font-bold md:mb-6 px-4">{t(FORM_TITLE_KEYS[contentType])}</SheetTitle>
          <div className="absolute top-6 right-4 bg-white">
            <CloseButton onClick={() => onOpenChange(false)} size="md" />
          </div>
          {renderFormContent()}
          {renderButtons('sheet')}
          {renderConfirmDialog()}
        </SheetContent>
      </Sheet>
    );
  }

  if (type === null) {
    return (
      <div className="relative border rounded-md p-4">
        <h2 className="text-2xl font-bold mb-4">{t(FORM_TITLE_KEYS[contentType])}</h2>
        {renderFormContent()}
        {renderButtons('inline')}
        {renderConfirmDialog()}
      </div>
    );
  }

  return null;
}

export default ArtworkDetailsForm;
